import { format, formatDistanceStrict } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import * as React from 'react'
import '../alerts.css'
import {
  AlertColumnName,
  AlertLevel,
  AlertMessage,
  AlertTableSortSettings,
  AlertViewSettings,
  ClientExposuresTransactions, getPageUrl,
  LimitType,
  MutedClientMetricsDict,
  Permission,
  SortType,
  User,
} from '../types/types'
import {
  assertNever,
  defaultMetricMutes,
  generateKeyFrom,
  getNewLevel,
  getOldLevel,
  obfuscateSymbol,
} from '../utils'
import {Link} from "react-router-dom";
import { ClientCode } from "../types/clients";
import isEqual from 'lodash/isEqual';

export interface Props {
  readonly alerts: ReadonlyArray<AlertMessage>
  readonly clientCodesFromAlerts: ReadonlySet<ClientCode>
  readonly viewSettings: AlertViewSettings
  readonly localTime: Date
  readonly mutedClientMetrics: MutedClientMetricsDict
  readonly toggleMenuFor: (column: AlertColumnName) => () => void
  readonly setColumnSort: (
    field: keyof AlertMessage,
    sortType: SortType,
  ) => () => void
  readonly filterClientColumn: (
    filter: AlertViewSettings['filterClient'],
  ) => () => void
  readonly filterDirectionColumn: (
    filter: AlertViewSettings['filterDirection'],
  ) => () => void
  readonly filterSeverityColumn: (
    filter: AlertViewSettings['filterSeverityOrLevel'],
  ) => () => void
  readonly filterMetricColumn: (
    filter: AlertViewSettings['filterMetric'],
  ) => () => void
  readonly clearFilters: () => void
  readonly toggleMute: (clientCode: ClientCode, metric: LimitType) => () => void
  readonly toggleShowOnlyActiveAlerts: () => void
  readonly showRedButtonFor: (clientCode: ClientCode) => () => void
  readonly hasPermission: {
    (permission: Permission): (user: User) => boolean
    (permission: Permission, user: User): boolean
  }
  readonly user: User
  readonly clients: ReadonlyArray<ClientExposuresTransactions>
}

// How to import the formatDistanceStrict options type from date-fns?
const dateFormatOptions = {
  addSuffix: true,
}

function ManageAlerts(props: Props): JSX.Element {
  const {
    alerts,
    clearFilters,
    filterClientColumn,
    toggleShowOnlyActiveAlerts,
    viewSettings,
  } = props
  const columns = generateColumns(props)
  const renderAlertRow = renderAlertRowClosure(props, columns)
  const noColumnFiltersInUse =
    viewSettings.filterDirection === 'all' &&
    isEqual(viewSettings.filterSeverityOrLevel, ['all']) &&
    viewSettings.filterMetric === 'all' &&
    viewSettings.filterClient === undefined
  const title =
    viewSettings.filterClient === undefined
      ? 'Alerts'
      : `Alerts for ${viewSettings.filterClient}`

  return (
    <>
      <h1 className='no-margin-top'>{title}</h1>
      <div className='alerts-page-main-controls'>
        <button onClick={toggleShowOnlyActiveAlerts} className={'button'}>
          {viewSettings.showOnlyActiveAlerts
            ? 'Show active & inactive alerts'
            : 'Show only active alerts'}
        </button>

        <button
          onClick={clearFilters}
          className={'button'}
          disabled={noColumnFiltersInUse}
        >
          Clear column filters
        </button>
      </div>
      <table className='theme--black-on-white theme--black-on-white--no-solid-backgrounds'>
        <thead>
          <tr>{columns.map(col => col.header())}</tr>
        </thead>
        <tbody>{alerts.map(renderAlertRow)}</tbody>
      </table>
      {alerts.length > 0
        ? selectOtherClientMessage(
            viewSettings.filterClient,
            filterClientColumn(undefined),
          )
        : noAlertsMessage(
            viewSettings.filterClient,
            filterClientColumn(undefined),
          )}
    </>
  )
}

function generateColumns(props: Props): ReadonlyArray<Column> {
  const {
    localTime,
    mutedClientMetrics,
    toggleMute,
    viewSettings,
    showRedButtonFor,
    filterClientColumn,
    hasPermission,
    user,
    clients,
  } = props
  const clientCodes = clients.map(c => c.code)
  const canViewClientCodes = hasPermission('canViewMainChart', user)
  const canRedButton = hasPermission('canRedButton', user)
  return [
    {
      body: (alert: AlertMessage): JSX.Element =>
        canViewClientCodes ? (
          <td
            key='client'
            className='column--client clickable'
            onClick={filterClientColumn(alert.companyCode)}
          >
            {alert.companyCode}
          </td>
        ) : (
          <td key='client' className='column--client'>
            <span className='tv-mode-symbol'>
              {obfuscateSymbol(clientCodes.indexOf(alert.companyCode))}
            </span>
          </td>
        ),
      header: (): JSX.Element =>
        canViewClientCodes ? (
          <th key='client' className='column--client'>
            Client
            {dropdownMenuFor(
              props,
              'client',
              filterIndicatorForClient(viewSettings.filterClient),
              sortIndicator(viewSettings, 'companyCode'),
            )}
          </th>
        ) : (
          <th key='client' className='column--client'>
            Client
          </th>
        ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td key='when' className='column--when'>
          {showConvenientTime(localTime, alert.rjeMessageTimestamp)}
        </td>
      ),
      header: (): JSX.Element => (
        <th key='when' className='column--when'>
          When
          {dropdownMenuFor(
            props,
            'when',
            null,
            sortIndicator(viewSettings, 'rjeMessageTimestamp'),
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td
          key='direction'
          className={'column--direction exceeded--' + alert.exceeded}
        >
          {alert.exceeded ? '▲' : <>&#xea3e;</>}
        </td>
      ),
      header: (): JSX.Element => (
        <th key='direction' className='column--direction'>
          Direction
          {dropdownMenuFor(
            props,
            'direction',
            filterIndicator(viewSettings, 'filterDirection'),
            null,
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td
          key='levels'
          className={`column--levels column--levels--${oldLevelOf(alert)}${
            alert.exceeded ? '↗' : '↘'
          }${newLevelOf(alert)}`}
        >
          {oldLevelOf(alert)} {alert.exceeded ? '↗' : '↘'} {newLevelOf(alert)}
        </td>
      ),
      header: (): JSX.Element => (
        <th key='levels' className='column--levels'>
          Levels
          {dropdownMenuFor(
            props,
            'levels',
            filterIndicator(viewSettings, 'filterSeverityOrLevel'),
            null,
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td key='thresholdPercent' className='column--thresholdPercent'>
          {alert.thresholdPercent != null ? `${alert.thresholdPercent.toLocaleString()}%` : null}
        </td>
      ),
      header: (): JSX.Element => (
        <th key='thresholdPercent' className='column--thresholdPercent'>
          Limit %
          {dropdownMenuFor(
            props,
            'thresholdPercent',
            null,
            sortIndicator(viewSettings, 'thresholdPercent'),
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td key='triggerPercent' className='column--triggerPercent'>
          {alert.percentValue != null ? `${alert.percentValue.toLocaleString()}%` : null}
        </td>
      ),
      header: (): JSX.Element => (
        <th
          key='triggerPercent'
          className='column--triggerPercent'
          title='The value of the metric when the alert was triggered'
        >
          Trigger %
          {dropdownMenuFor(
            props,
            'triggerPercent',
            null,
            sortIndicator(viewSettings, 'percentValue'),
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td key='rjeMessageNumber' className='column--rjeMessageNumber'>
          {alert.rjeMessageNumber}
        </td>
      ),
      header: (): JSX.Element => (
        <th key='rjeMessageNumber' className='column--rjeMessageNumber'>
          RJE Msg#
          {dropdownMenuFor(
            props,
            'rjeMessageNumber',
            null,
            sortIndicator(viewSettings, 'rjeMessageNumber'),
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => (
        <td key='metric' className='column--metric'>
          {alert.limitType}
        </td>
      ),
      header: (): JSX.Element => (
        <th key='metric' className='column--metric'>
          RC
          {dropdownMenuFor(
            props,
            'metric',
            filterIndicator(viewSettings, 'filterMetric'),
            null,
          )}
        </th>
      ),
    },
    {
      body: (alert: AlertMessage): JSX.Element =>
        canRedButton ? (
          <td key='show-red-button' className='column--show-red-button'>
            <Link
              to={getPageUrl("Red Buttons")}
              className='button'
              onClick={showRedButtonFor(alert.companyCode)}
            >
              Begin Red Button Process
            </Link>
          </td>
        ) : (
          <></>
        ),
      header: (): JSX.Element =>
        canRedButton ? (
          <th key='show-red-button' className='column--show-red-button' />
        ) : (
          <></>
        ),
    },
    {
      body: (alert: AlertMessage): JSX.Element => {
        const mutedMetrics =
          mutedClientMetrics[alert.companyCode] || defaultMetricMutes

        const isMuted = mutedMetrics[alert.limitType] === 'muted'

        return (
          <td key='toggle-mute' className='column--toggle-mute'>
            <button
              className='button'
              onClick={toggleMute(alert.companyCode, alert.limitType)}
            >
              {isMuted ? 'Un-mute' : 'Mute'}
            </button>
          </td>
        )
      },
      header: (): JSX.Element => (
        <th key='toggle-mute' className='column--toggle-mute' />
      ),
    },
  ]
}

function renderAlertRowClosure(
  props: Props,
  columns: ReadonlyArray<Column>,
): (alert: AlertMessage) => JSX.Element {
  return function renderAlertRow(alert: AlertMessage): JSX.Element {
    return (
      <tr
        className={`alert-row alert-row--${
          alert.alertSeverity
        } alert-row--is-exceeded-${alert.exceeded}`}
        key={generateKeyFrom(alert)}
      >
        {columns.map(col => col.body(alert))}
      </tr>
    )
  }
}

/**
 * Looks like "9 minutes ago (09:49:02 Thu 14th Feb JST)"
 */
function showConvenientTime(localTime: Date, date: Date): string {
  return (
    formatDistanceStrict(date, localTime, dateFormatOptions) +
    ' (' +
    formatInTimeZone(date, 'Asia/Tokyo', 'HH:mm:ss EEE do MMM') +
    ' JST)'
  )
}

function noAlertsMessage(
  selected: ClientCode | undefined,
  filterClientColumn: () => void,
): JSX.Element {
  if (selected === undefined) {
    return <p>No alerts at the moment.</p>
  }
  return (
    <p>
      No alerts for "{selected}" at the moment.{' '}
      <button onClick={filterClientColumn} className={'button'}>
        Show all client alerts.
      </button>
    </p>
  )
}

function selectOtherClientMessage(
  filteredClient: ClientCode | undefined,
  filterClientColumn: () => void,
): JSX.Element | null {
  if (filteredClient === undefined) {
    return null
  }
  return (
    <p>
      Showing alerts for "{filteredClient}".{' '}
      <button onClick={filterClientColumn} className={'button'}>
        Show all client alerts.
      </button>
    </p>
  )
}

/**
 * Show the value of a metric with the units it's measured in, for context.
 */
export function unitize(metric: LimitType, value: number): string {
  switch (metric) {
    case 'RC1_total_clip_size':
    case 'RC2_exposure':
    case 'Realistic_exposure_value':
    case 'Historic_Max_Realistic_exposure':
      return `¥${value.toLocaleString()}`
    case 'RC6_unactioned_orders':
      return value.toLocaleString()
    case 'RC7_messages_per_second':
      return `${value.toLocaleString()} msgs / sec`
    case "Virtual_server_suspension":
      return '';
    default:
      return assertNever(metric)
  }
}

function dropdownMenuFor(
  props: Props,
  column: AlertColumnName,
  filterIcon: JSX.Element | null,
  sortIcon: JSX.Element | null,
): JSX.Element {
  const { toggleMenuFor, viewSettings } = props
  const isActive =
    viewSettings.openColumnMenu !== undefined &&
    viewSettings.openColumnMenu === column

  const hasFilterOrSort = filterIcon !== null || sortIcon !== null

  const contents = isActive ? (
    <div className='column-dropdown'>{menuContentsFor(props, column)}</div>
  ) : null

  const outsideClickDetector = isActive ? (
    <div
      className='column-menu-dropdown__outside-detector'
      onClick={toggleMenuFor(column)}
    />
  ) : (
    <></>
  )

  return (
    <>
      {sortIcon}
      {filterIcon} {outsideClickDetector}
      <button
        className={`button column-menu-dropdown column-menu-dropdown--active-${
          isActive ? 'true' : 'false'
        } ${hasFilterOrSort ? 'column-filter-active' : ''}`}
        onClick={toggleMenuFor(column)}
      >
        &#xf078;{contents}
      </button>
    </>
  )
}

function menuContentsFor(
  {
    setColumnSort,
    filterClientColumn,
    filterDirectionColumn,
    filterSeverityColumn,
    filterMetricColumn,
    clientCodesFromAlerts,
  }: Props,
  column: AlertColumnName,
): JSX.Element | null {
  switch (column) {
    case 'when':
    case 'sequenceNumber':
    case 'rjeMessageNumber':
      return (
        <>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'descending')}
          >
            Sort Latest → Oldest
          </div>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'ascending')}
          >
            Sort Oldest → Latest
          </div>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'none')}
          >
            Clear Sort
          </div>
        </>
      )
    case 'direction':
      return (
        <>
          <div
            className='column-dropdown-item'
            onClick={filterDirectionColumn('exceeding')}
          >
            Show only ▲
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterDirectionColumn('retreating from')}
          >
            Show only &#xea3e;
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterDirectionColumn('all')}
          >
            Clear filter
          </div>
        </>
      )
    case 'client':
      return (
        <>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'ascending')}
          >
            Sort A → Z
          </div>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'descending')}
          >
            Sort Z → A
          </div>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'none')}
          >
            Clear Sort
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterClientColumn(undefined)}
          >
            Show all clients
          </div>
          {[...clientCodesFromAlerts].map(toDropdownEntry)}
        </>
      )

    case 'metric':
      return (
        <>
          <div
            className='column-dropdown-item'
            onClick={filterMetricColumn('RC2_exposure')}
          >
            Show only RC2 "Exposure"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterMetricColumn('RC6_unactioned_orders')}
          >
            Show only RC6 "Unactioned orders"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterMetricColumn('Realistic_exposure_value')}
          >
            Show only REV "Realistic exposure value"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterMetricColumn('Virtual_server_suspension')}
          >
            Show only "Virtual server suspensions"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterMetricColumn('all')}
          >
            Clear filter
          </div>
        </>
      )
    case 'levels':
      return (
        <>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['all'])}
          >
            Clear filter
          </div>
          <div className='column-dropdown-item'>
            <b>Resulting Level</b>
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['level', 'Breach'])}
          >
            Show only "Breach"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['level', 'Warn'])}
          >
            Show only "Warn"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['level', 'Notify'])}
          >
            Show only "Notify"
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['level', 'Baseline'])}
          >
            Show only "Baseline"
          </div>
          <div className='column-dropdown-item'>
            <b>Threshold Crossed</b>
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['severity', 'Breach'])}
          >
            Show only "Breach" crossings
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['severity', 'Warn'])}
          >
            Show only "Warn" crossings
          </div>
          <div
            className='column-dropdown-item'
            onClick={filterSeverityColumn(['severity', 'Notify'])}
          >
            Show only "Notify" crossings
          </div>
        </>
      )
    case 'thresholdPercent':
    case 'triggerPercent':
      return (
        <>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'descending')}
          >
            Sort Highest → Lowest
          </div>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'ascending')}
          >
            Sort Lowest → Highest
          </div>
          <div
            className='column-dropdown-item'
            onClick={setColumnSort(mapToField(column), 'none')}
          >
            Clear Sort
          </div>
        </>
      )
    default:
      return assertNever(column)
  }

  function toDropdownEntry(c: ClientCode): JSX.Element {
    return (
      <div
        className='column-dropdown-item'
        onClick={filterClientColumn(c)}
        key={c}
      >
        {c}
      </div>
    )
  }
}

function mapToField(column: AlertColumnName): keyof AlertMessage {
  switch (column) {
    case 'client':
      return 'companyCode'
    case 'direction':
      return 'exceeded'
    case 'levels':
      return 'alertSeverity'
    case 'metric':
      return 'limitType'
    case 'when':
      return 'rjeMessageTimestamp'
    case 'triggerPercent':
      return 'percentValue'
    case 'sequenceNumber':
    case 'rjeMessageNumber':
    case 'thresholdPercent':
      return column
    default:
      return assertNever(column)
  }
}

function isSortingBy(
  sortBy: AlertTableSortSettings,
  column: keyof AlertMessage,
): boolean {
  return sortBy.map(([col, _]) => col).includes(column)
}

function sortIndicator(
  viewSettings: AlertViewSettings,
  column: keyof AlertMessage,
): JSX.Element | null {
  if (isSortingBy(viewSettings.sortBy, column)) {
    return (
      <span className='column-sort-indicator column-sort-indicator--active'>
        ⇅
      </span>
    )
  }
  return null
}

const filterIndicatorIcon = (
  <span className='column-filter-indicator column-filter-indicator--active'>
    &#xea5b;
  </span>
)

function filterIndicator(
  viewSettings: AlertViewSettings,
  filter: keyof Pick<
    AlertViewSettings,
    'filterDirection' | 'filterSeverityOrLevel' | 'filterMetric'
  >,
): JSX.Element | null {
  if (filter === 'filterSeverityOrLevel' && viewSettings[filter][0] === 'all') {
    return null
  }

  if (viewSettings[filter] !== 'all') {
    return filterIndicatorIcon
  }
  return null
}

function filterIndicatorForClient(
  clientCodeFilter: ClientCode | undefined,
): JSX.Element | null {
  if (clientCodeFilter === undefined) {
    return null
  }
  return filterIndicatorIcon
}

interface Column {
  readonly header: () => JSX.Element
  readonly body: (alert: AlertMessage) => JSX.Element
}

const oldLevelOf = (alert: AlertMessage): AlertLevel =>
  getOldLevel(alert.alertSeverity, alert.exceeded, alert.limitType)

const newLevelOf = (alert: AlertMessage): AlertLevel =>
  getNewLevel(alert.alertSeverity, alert.exceeded, alert.limitType)

export default ManageAlerts
